import type { CompanySessionAdmission } from "../api/companySession";
import { hasPreloadLife } from "../contexts/tabCompanySession";
import { clearOnSignOut } from "./signOutCleanup";
import { tenantRoute } from "./tenantRoute";

/**
 * A project switch reloads the page. The admission the switcher prefetched for the target
 * (`contexts/admissionPrefetch.ts`) crosses that reload once, in this tab's sessionStorage:
 * staged on the click and written only by the unload of the navigation it was staged for
 * (`stageAdmissionHandoff`), so a leave the page cancels stores nothing, then or later; taken
 * out of storage by the next Document's bootstrap whatever its route (`claimAdmissionHandoff`);
 * and used there only by the first admission of the route and source session it was asked for,
 * while it is fresh. It carries a project credential, so it never goes to localStorage, a log or
 * analytics, and sign-out deletes it (`utils/logout.ts`).
 */
export const ADMISSION_HANDOFF_KEY = "pp.admissionHandoff";

/** A handoff older than this, counted from when its answer arrived, is not used. */
const HANDOFF_TTL_MS = 30_000;

export interface AdmissionRoute {
    companySlug: string;
    projectSlug: string;
}

/** A company-session answer, its credential's local deadline (`credentialDeadline`) and when it
 *  arrived, all on this browser's clock. */
export interface ReceivedAdmission {
    value: CompanySessionAdmission;
    deadline: number;
    receivedAt: number;
}

interface AdmissionHandoff extends ReceivedAdmission {
    route: AdmissionRoute;
    /** The source session it was asked for, `organization_id:member_id:member_session_id`. */
    source: string;
}

type RouteSlugs = { companySlug: string | null; projectSlug: string | null };

/** The handoff a leave staged, written when the navigation it was staged for unloads this
 *  Document. */
let staged: AdmissionHandoff | null = null;
/** Set once the staged navigation's own `beforeunload` has run. */
let unloading = false;
/** The handoff this Document's bootstrap took out of storage, kept for its first admission. */
let carried: AdmissionHandoff | null = null;

/** Whether an answer may still be handed over, or used once handed. */
export function isFreshAdmission(admission: ReceivedAdmission, now = Date.now()): boolean {
    const age = now - admission.receivedAt;
    return age >= 0 && age < HANDOFF_TTL_MS && hasPreloadLife(admission.deadline, now);
}

const isFor = (handoff: AdmissionHandoff, route: RouteSlugs) =>
    handoff.route.companySlug === route.companySlug &&
    handoff.route.projectSlug === route.projectSlug;

function dropStagedHandoff(): void {
    staged = null;
    unloading = false;
    window.removeEventListener("beforeunload", passBeforeUnload);
    window.removeEventListener("pagehide", commitStagedHandoff);
}

/** The first `beforeunload` after staging is the staged navigation's own. A second one means that
 *  navigation never unloaded this Document (a prompt answered Stay, a load stopped, a newer leave)
 *  and this unload is another: a tab close, a reload, a move to another site. */
function passBeforeUnload(): void {
    if (unloading) dropStagedHandoff();
    else unloading = true;
}

function commitStagedHandoff(): void {
    const handoff = staged;
    dropStagedHandoff();
    if (!handoff || !isFreshAdmission(handoff)) return;
    try {
        window.sessionStorage.setItem(ADMISSION_HANDOFF_KEY, JSON.stringify(handoff));
    } catch {
        /* Without storage the next page admits as it would without a prefetch. */
    }
}

/**
 * On a leave, just before it navigates: hands `handoff` to the next Document once that navigation
 * unloads this one (`pagehide`). A leave that does not happen writes nothing, whatever unloads
 * this Document later: the page cannot read a `beforeunload` prompt's answer, so the handoff
 * belongs to the first `beforeunload` after it was staged and is dropped at the next. No timer
 * drops it, because a slow navigation keeps this Document until its response commits. A browser
 * that runs no `beforeunload` has no prompt to stay on either; there the next `pagehide` writes.
 */
export function stageAdmissionHandoff(handoff: AdmissionHandoff): void {
    dropStagedHandoff();
    staged = handoff;
    window.addEventListener("beforeunload", passBeforeUnload);
    window.addEventListener("pagehide", commitStagedHandoff);
}

function parseHandoff(stored: string): AdmissionHandoff | null {
    try {
        const handoff = JSON.parse(stored) as Partial<AdmissionHandoff> | null;
        if (
            !handoff ||
            typeof handoff.source !== "string" ||
            typeof handoff.route?.companySlug !== "string" ||
            typeof handoff.route?.projectSlug !== "string" ||
            !handoff.value ||
            typeof handoff.value !== "object" ||
            typeof handoff.deadline !== "number" ||
            typeof handoff.receivedAt !== "number"
        )
            return null;
        return {
            route: {
                companySlug: handoff.route.companySlug,
                projectSlug: handoff.route.projectSlug,
            },
            source: handoff.source,
            value: handoff.value,
            deadline: handoff.deadline,
            receivedAt: handoff.receivedAt,
        };
    } catch {
        return null;
    }
}

/**
 * Document bootstrap, before anything else reads the URL or storage: takes any carried handoff
 * out of storage, on every route. It is kept for the first admission only when it is for this
 * Document's own project route and fresh; one for another route, stale or that cannot be
 * deleted is dropped unused.
 */
export function claimAdmissionHandoff(route: RouteSlugs = tenantRoute()): void {
    carried = null;
    let stored: string | null;
    try {
        stored = window.sessionStorage.getItem(ADMISSION_HANDOFF_KEY);
        if (stored === null) return;
        window.sessionStorage.removeItem(ADMISSION_HANDOFF_KEY);
    } catch {
        return;
    }
    const handoff = parseHandoff(stored);
    if (handoff && isFor(handoff, route) && isFreshAdmission(handoff)) carried = handoff;
}

/** The claimed handoff for `route` and `source`, taken once: whatever it holds, it is gone. */
export function takeAdmissionHandoff(
    route: RouteSlugs,
    source: string | null,
): ReceivedAdmission | null {
    const handoff = carried;
    carried = null;
    if (!handoff || !source || handoff.source !== source || !isFor(handoff, route)) return null;
    const { value, deadline, receivedAt } = handoff;
    return isFreshAdmission(handoff) ? { value, deadline, receivedAt } : null;
}

clearOnSignOut(() => {
    dropStagedHandoff();
    carried = null;
});
